import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, unref as _unref, createVNode as _createVNode, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "premium-paywall relative overflow-hidden rounded-t-3xl bg-brand-black text-white max-h-[92vh] overflow-y-auto" }
const _hoisted_2 = { class: "relative px-5 pt-4 pb-6" }
const _hoisted_3 = { class: "flex items-start justify-between gap-3 mb-5" }
const _hoisted_4 = { class: "flex items-center gap-3 min-w-0" }
const _hoisted_5 = { class: "min-w-0" }
const _hoisted_6 = { class: "text-2xl font-medium leading-tight m-0 mt-0.5" }
const _hoisted_7 = { class: "text-base leading-relaxed text-brand-light-gray mb-5" }
const _hoisted_8 = { class: "mb-6 space-y-2.5" }
const _hoisted_9 = { class: "min-w-0 pt-0.5" }
const _hoisted_10 = { class: "text-sm font-medium text-white leading-snug" }
const _hoisted_11 = { class: "text-xs text-brand-light-gray mt-0.5 leading-snug" }
const _hoisted_12 = {
  key: 0,
  class: "py-10 flex justify-center"
}
const _hoisted_13 = {
  key: 1,
  class: "space-y-4"
}
const _hoisted_14 = { class: "rounded-2xl bg-brand-dark-gray ring-1 ring-white/10 px-4 py-4" }
const _hoisted_15 = { class: "text-lg font-medium capitalize" }
const _hoisted_16 = {
  key: 0,
  class: "mt-3 text-sm text-brand-light-gray"
}
const _hoisted_17 = {
  key: 1,
  class: "mt-2 text-sm text-brand-light-gray"
}
const _hoisted_18 = {
  key: 2,
  class: "mt-3"
}
const _hoisted_19 = ["href"]
const _hoisted_20 = { class: "space-y-2" }
const _hoisted_21 = ["disabled"]
const _hoisted_22 = ["disabled"]
const _hoisted_23 = ["disabled"]
const _hoisted_24 = {
  key: 1,
  class: "rounded-xl bg-brand-pink/10 px-3 py-2 text-sm text-brand-pink"
}
const _hoisted_25 = {
  key: 2,
  class: "space-y-3"
}
const _hoisted_26 = { class: "rounded-2xl bg-brand-dark-gray ring-2 ring-brand-lime px-4 py-4" }
const _hoisted_27 = { class: "font-medium text-white" }
const _hoisted_28 = { class: "text-lg font-semibold mt-1" }
const _hoisted_29 = { class: "grid grid-cols-2 gap-2" }
const _hoisted_30 = { class: "flex items-start gap-2 text-xs text-brand-light-gray leading-relaxed" }
const _hoisted_31 = ["disabled"]
const _hoisted_32 = {
  key: 0,
  class: "flex items-center gap-2"
}
const _hoisted_33 = { key: 1 }
const _hoisted_34 = {
  key: 0,
  class: "rounded-xl bg-brand-pink/10 px-3 py-2 text-sm text-brand-pink"
}

import {computed, onMounted, ref} from 'vue';
import {IonSpinner, modalController, toastController, useIonRouter} from '@ionic/vue';
import {billingService} from '@/services/billing/billingService';
import {Observable, ObservableEvent} from '@/services/observable/Observable';

type Benefit = {
  id: string;
  title: string;
  detail: string;
  keywords: string[];
  highlight?: boolean;
  score?: number;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'PremiumCheckoutPage',
  props: {
  title: {type: String, default: ''},
  text: {type: String, default: ''},
  placement: {type: String, default: 'premium'},
},
  setup(__props) {

const props = __props;

const ionRouter = useIonRouter();

const ALL_BENEFITS: Benefit[] = [
  {id: 'nsfw', title: 'Unlimited NSFW & mature content', detail: 'No daily reveal caps — see and share without interruption.', keywords: ['nsfw', 'x-rated', 'mature', 'blur', 'reveal', 'photo', 'video']},
  {id: 'chat', title: 'Message without limits', detail: 'Start conversations and keep chatting as much as you want.', keywords: ['message', 'messages', 'chat', 'direct', 'dm', 'conversation']},
  {id: 'gallery', title: 'Full galleries & pinch-zoom', detail: 'Open private albums and view photos properly, not cropped.', keywords: ['gallery', 'albums', 'photos', 'pinch', 'zoom', 'fullscreen']},
  {id: 'views', title: 'See who viewed your profile', detail: 'Know who’s checking you out and follow up while it’s hot.', keywords: ['view', 'views', 'looking', 'viewer', 'viewers']},
  {id: 'events', title: 'Host events & see guest lists', detail: 'Create gatherings and know exactly who’s coming.', keywords: ['event', 'events', 'host', 'guest', 'ticket']},
  {id: 'nearby', title: 'Explore further nearby', detail: 'Widen your radius and find more people and clubs around you.', keywords: ['nearby', 'explore', 'afield', 'radius', 'map', 'distance']},
];

const loading = ref(true);
const checkingOut = ref(false);
const polling = ref(false);
const error = ref('');
const billingStatus = ref<any>(null);
const firstName = ref('');
const lastName = ref('');
const discountCode = ref('');
const consentAccepted = ref(false);
const consentCopy = ref('');
const planMeta = ref<any>(null);

const hasProfile = computed(() => Boolean(billingStatus.value && billingStatus.value.status && billingStatus.value.status !== 'none' && billingStatus.value.status !== 'card_required'));

const contextBlob = computed(() => `${props.title || ''} ${props.text || ''}`.toLowerCase());
const headline = computed(() => {
  if (props.title?.trim()) return props.title.trim();
  if (/message|chat|dm/.test(contextBlob.value)) return 'Keep the conversation going';
  if (/nsfw|x-rated|blur|mature|reveal/.test(contextBlob.value)) return 'See everything, no limits';
  if (/gallery|pinch|zoom|photo/.test(contextBlob.value)) return 'Unlock the full gallery';
  if (/view|looking/.test(contextBlob.value)) return 'Find out who’s looking';
  if (/event|host|guest/.test(contextBlob.value)) return 'Host like a pro';
  if (/nearby|explore|afield|radius/.test(contextBlob.value)) return 'Widen your world';
  return 'Join Pineapple Premium';
});
const supportingText = computed(() => props.text?.trim() || 'Get the full lifestyle experience — unlimited reveals, messaging, galleries, events, and more.');

const benefits = computed(() => {
  const blob = contextBlob.value;
  const scored = ALL_BENEFITS.map((benefit) => {
    const hit = benefit.keywords.some((kw) => blob.includes(kw));
    return {...benefit, highlight: hit, score: hit ? 1 : 0};
  });
  scored.sort((a, b) => (b.score || 0) - (a.score || 0));
  const highlighted = scored.filter((b) => b.highlight);
  const rest = scored.filter((b) => !b.highlight);
  const picked = [...highlighted, ...rest].slice(0, 4);
  if (!picked.some((b) => b.highlight) && picked[0]) picked[0] = {...picked[0], highlight: true};
  return picked;
});

const planLabel = computed(() => planMeta.value?.name || 'Pineapple Premium');
const planPrice = computed(() => {
  if (planMeta.value?.priceLabel) return planMeta.value.priceLabel;
  if (billingStatus.value?.list_amount != null) {
    return `${(Number(billingStatus.value.list_amount) / 100).toFixed(2)} ${billingStatus.value.currency || 'GBP'} / month`;
  }
  return '9.99 GBP / month';
});

const nextDateLabel = computed(() => {
  const s = billingStatus.value;
  if (!s) return '';
  if (s.status === 'trialing' && s.trial_ends_at) return `Trial ends ${new Date(s.trial_ends_at).toLocaleDateString()}`;
  if (s.grace_until) return `Grace until ${new Date(s.grace_until).toLocaleDateString()}`;
  if (s.current_period_end) return `Next charge ${new Date(s.current_period_end).toLocaleDateString()}`;
  return '';
});

const dismiss = async () => {
  const top = await modalController.getTop();
  if (top) {
    await modalController.dismiss();
    return;
  }

  // Full-page return from partner checkout has no modal / back stack.
  await ionRouter.navigate({name: 'Feed'}, 'root', 'replace');
};

const load = async () => {
  loading.value = true;
  error.value = '';
  try {
    const params = new URLSearchParams(window.location.search);
    const setup = params.get('setup');

    billingStatus.value = await billingService.status().catch(() => ({status: 'none'}));
    const plans = await billingService.plans();
    planMeta.value = Array.isArray(plans) ? plans[0] : null;

    if (setup === 'success') {
      await pollUntilActive();
    } else if (setup === 'failed') {
      error.value = 'Card setup failed. Try again.';
    }
  } catch (e: any) {
    if (!billingStatus.value || billingStatus.value.status === 'none') {
      error.value = e?.message || 'Could not load billing';
    }
  } finally {
    loading.value = false;
  }
};

const pollUntilActive = async (attempts = 12) => {
  polling.value = true;
  for (let i = 0; i < attempts; i++) {
    const ok = await billingService.syncSubscriptionStatus();
    billingStatus.value = await billingService.status().catch(() => billingStatus.value);
    if (ok || ['trialing', 'active'].includes(billingStatus.value?.status)) {
      polling.value = false;
      const toast = await toastController.create({
        message: 'Welcome to Pineapple Premium',
        duration: 3000,
        color: 'primary',
      });
      await toast.present();
      Observable.emit(ObservableEvent.UserChanged);
      await dismiss();
      return;
    }
    await new Promise((r) => setTimeout(r, 2500));
  }
  polling.value = false;
  error.value = 'Card not confirmed yet. If you finished checkout, reopen this screen in a moment.';
};

const checkout = async () => {
  if (!firstName.value.trim() || !lastName.value.trim()) {
    error.value = 'Enter your legal first and last name';
    return;
  }
  if (!consentAccepted.value) {
    error.value = 'Accept the billing terms to continue';
    return;
  }
  checkingOut.value = true;
  error.value = '';
  try {
    const returnUrl = `${window.location.origin}/account/subscription`;
    const result = await billingService.checkout({
      first_name: firstName.value.trim(),
      last_name: lastName.value.trim(),
      discount_code: discountCode.value.trim() || undefined,
      consent_accepted: true,
      return_url: returnUrl,
    });
    consentCopy.value = result?.consent_copy || consentCopy.value;
    if (!result?.checkout_url) {
      error.value = 'Could not start checkout';
      return;
    }
    window.location.assign(result.checkout_url);
  } catch (e: any) {
    error.value = e?.message || 'Checkout failed';
  } finally {
    checkingOut.value = false;
  }
};

const cancel = async () => {
  checkingOut.value = true;
  try {
    await billingService.cancelSubscription();
    billingStatus.value = await billingService.status();
    const toast = await toastController.create({
      message: 'Subscription will end at the current period',
      duration: 3000,
      color: 'medium',
    });
    await toast.present();
  } catch (e: any) {
    error.value = e?.message || 'Cancel failed';
  } finally {
    checkingOut.value = false;
  }
};

const updateCard = async () => {
  checkingOut.value = true;
  error.value = '';
  try {
    const result = await billingService.updatePaymentMethod(`${window.location.origin}/account/subscription`);
    if (!result?.checkout_url) {
      error.value = 'Could not start card update';
      return;
    }
    window.location.assign(result.checkout_url);
  } catch (e: any) {
    error.value = e?.message || 'Card update failed';
  } finally {
    checkingOut.value = false;
  }
};

const applyDiscount = async () => {
  checkingOut.value = true;
  error.value = '';
  try {
    await billingService.applyDiscount(discountCode.value.trim());
    discountCode.value = '';
    const toast = await toastController.create({
      message: 'Discount saved for the next charge',
      duration: 2500,
      color: 'primary',
    });
    await toast.present();
  } catch (e: any) {
    error.value = e?.message || 'Discount failed';
  } finally {
    checkingOut.value = false;
  }
};

onMounted(load);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[12] || (_cache[12] = _createElementVNode("div", { class: "pointer-events-none absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-brand-lime/15 via-brand-lime/5 to-transparent" }, null, -1)),
    _cache[13] || (_cache[13] = _createElementVNode("div", { class: "pointer-events-none absolute -right-16 -top-10 h-40 w-40 rounded-full bg-brand-lime/10 blur-3xl" }, null, -1)),
    _cache[14] || (_cache[14] = _createElementVNode("div", { class: "pointer-events-none absolute -left-10 top-24 h-28 w-28 rounded-full bg-brand-lime/5 blur-2xl" }, null, -1)),
    _createElementVNode("div", _hoisted_2, [
      _cache[11] || (_cache[11] = _createElementVNode("div", { class: "mx-auto mb-4 h-1 w-10 rounded-full bg-white/20" }, null, -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _cache[6] || (_cache[6] = _createElementVNode("div", { class: "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-lime text-brand-black shadow-[0_0_24px_rgba(235,255,0,0.25)]" }, [
            _createElementVNode("svg", {
              class: "fill-current w-7 h-7",
              viewBox: "0 0 29 30",
              xmlns: "http://www.w3.org/2000/svg",
              "aria-hidden": "true"
            }, [
              _createElementVNode("path", {
                "fill-rule": "evenodd",
                "clip-rule": "evenodd",
                d: "M8.17261 2.76396C10.017 3.04821 11.8873 2.44094 13.2116 1.12627C13.7382 0.606218 14.3002 0.325195 14.8881 0.325195C15.3629 0.325195 15.8216 0.509314 16.2577 0.85171C16.3675 0.938924 16.4741 1.03583 16.5807 1.14242C17.905 2.43771 19.7624 3.03206 21.5906 2.74781C22.3271 2.65413 22.9538 2.75427 23.4318 3.10312C23.9067 3.44875 24.1974 4.00756 24.3169 4.73758C24.6173 6.57876 25.7737 8.17123 27.4308 9.02399C28.0865 9.36315 28.529 9.81214 28.7099 10.371C28.8908 10.9298 28.7971 11.5532 28.4644 12.2121C27.6246 13.8789 27.6246 15.8461 28.4644 17.5128C28.7971 18.1718 28.8908 18.7952 28.7099 19.354C28.529 19.9128 28.0865 20.3618 27.4308 20.701C25.7705 21.557 24.6173 23.1462 24.3169 24.9874C24.1974 25.7174 23.9067 26.2762 23.4318 26.6218C22.957 26.9675 22.3336 27.0708 21.6036 26.9578C19.7592 26.6735 17.8889 27.2808 16.5645 28.5955C16.038 29.1155 15.476 29.3965 14.8881 29.3965C14.3002 29.3965 13.7382 29.1155 13.2116 28.5955C11.8873 27.2808 10.017 26.6735 8.17261 26.9578C7.44259 27.0708 6.81917 26.9675 6.34434 26.6218C5.86951 26.2762 5.5788 25.7174 5.45928 24.9874C5.15888 23.1462 4.00248 21.5537 2.34542 20.701C1.6897 20.3618 1.24717 19.9128 1.06628 19.354C0.88539 18.7952 0.979064 18.1718 1.31177 17.5128C2.15161 15.8461 2.15161 13.8789 1.31177 12.2121C0.979064 11.5532 0.88539 10.9298 1.06628 10.371C1.24717 9.81214 1.6897 9.36315 2.34542 9.02399C4.00571 8.168 5.15888 6.57876 5.45928 4.73758C5.5788 4.00756 5.86951 3.44875 6.34434 3.10312C6.81917 2.7575 7.44259 2.65413 8.17261 2.76719V2.76396Z"
              }),
              _createElementVNode("path", {
                d: "M8.9248 14.9L12.8998 18.875L19.5248 10.925",
                stroke: "#1A1B22",
                "stroke-width": "1.9875",
                "stroke-linecap": "round",
                "stroke-linejoin": "round"
              })
            ])
          ], -1)),
          _createElementVNode("div", _hoisted_5, [
            _cache[5] || (_cache[5] = _createElementVNode("div", { class: "text-brand-lime text-xs font-semibold tracking-[0.14em] uppercase" }, "Pineapple Premium", -1)),
            _createElementVNode("h2", _hoisted_6, _toDisplayString(headline.value), 1)
          ])
        ]),
        _createElementVNode("button", {
          type: "button",
          class: "shrink-0 rounded-full px-3 py-1.5 text-sm text-brand-light-gray hover:text-white hover:bg-white/5 transition",
          onClick: dismiss
        }, " Close ")
      ]),
      _createElementVNode("p", _hoisted_7, _toDisplayString(supportingText.value), 1),
      _createElementVNode("ul", _hoisted_8, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(benefits.value, (benefit) => {
          return (_openBlock(), _createElementBlock("li", {
            key: benefit.id,
            class: _normalizeClass(["flex items-start gap-3 rounded-2xl px-3.5 py-2.5 transition", benefit.highlight
              ? 'bg-brand-lime/10 ring-1 ring-brand-lime/30'
              : 'bg-white/[0.03]'])
          }, [
            _createElementVNode("span", {
              class: _normalizeClass(["mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full", benefit.highlight ? 'bg-brand-lime text-brand-black' : 'bg-white/10 text-brand-lime'])
            }, [...(_cache[7] || (_cache[7] = [
              _createElementVNode("svg", {
                class: "w-3.5 h-3.5",
                viewBox: "0 0 16 16",
                fill: "none",
                "aria-hidden": "true"
              }, [
                _createElementVNode("path", {
                  d: "M3.5 8.5L6.5 11.5L12.5 4.5",
                  stroke: "currentColor",
                  "stroke-width": "2",
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round"
                })
              ], -1)
            ]))], 2),
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, _toDisplayString(benefit.title), 1),
              _createElementVNode("div", _hoisted_11, _toDisplayString(benefit.detail), 1)
            ])
          ], 2))
        }), 128))
      ]),
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
            _createVNode(_unref(IonSpinner), {
              name: "crescent",
              color: "light"
            })
          ]))
        : (hasProfile.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
              _createElementVNode("div", _hoisted_14, [
                _cache[8] || (_cache[8] = _createElementVNode("div", { class: "text-sm text-brand-light-gray" }, "Status", -1)),
                _createElementVNode("div", _hoisted_15, _toDisplayString(billingStatus.value?.status), 1),
                (billingStatus.value?.card)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_16, _toDisplayString(billingStatus.value.card.scheme) + " •••• " + _toDisplayString(billingStatus.value.card.last4), 1))
                  : _createCommentVNode("", true),
                (nextDateLabel.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_17, _toDisplayString(nextDateLabel.value), 1))
                  : _createCommentVNode("", true),
                (billingStatus.value?.continue_url)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                      _createElementVNode("a", {
                        href: billingStatus.value.continue_url,
                        class: "text-brand-lime text-sm underline"
                      }, "Complete card authentication", 8, _hoisted_19)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_20, [
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((discountCode).value = $event)),
                  type: "text",
                  placeholder: "Discount code for next renewal",
                  class: "w-full rounded-xl bg-brand-dark-gray border border-white/10 px-3 py-2.5 text-sm text-white"
                }, null, 512), [
                  [_vModelText, discountCode.value]
                ]),
                _createElementVNode("button", {
                  type: "button",
                  class: "w-full h-10 rounded-full bg-white/10 text-sm",
                  disabled: checkingOut.value || !discountCode.value.trim(),
                  onClick: applyDiscount
                }, " Apply discount ", 8, _hoisted_21)
              ]),
              _createElementVNode("button", {
                type: "button",
                class: "w-full flex h-12 items-center justify-center rounded-full bg-brand-lime px-6 text-lg font-medium text-brand-black disabled:opacity-50",
                disabled: checkingOut.value,
                onClick: updateCard
              }, " Update card ", 8, _hoisted_22),
              (!billingStatus.value?.cancel_at_period_end)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "w-full pt-1 text-center text-sm text-brand-light-gray underline-offset-2 hover:underline disabled:opacity-50",
                    disabled: checkingOut.value,
                    onClick: cancel
                  }, " Cancel subscription ", 8, _hoisted_23))
                : _createCommentVNode("", true),
              (error.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_24, _toDisplayString(error.value), 1))
                : _createCommentVNode("", true)
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_25, [
              _createElementVNode("div", _hoisted_26, [
                _createElementVNode("div", _hoisted_27, _toDisplayString(planLabel.value), 1),
                _createElementVNode("div", _hoisted_28, _toDisplayString(planPrice.value), 1),
                _cache[9] || (_cache[9] = _createElementVNode("div", { class: "text-xs text-brand-light-gray mt-1" }, "Billed monthly after your free trial", -1))
              ]),
              _createElementVNode("div", _hoisted_29, [
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((firstName).value = $event)),
                  type: "text",
                  placeholder: "First name",
                  class: "rounded-xl bg-brand-dark-gray border border-white/10 px-3 py-2.5 text-sm text-white"
                }, null, 512), [
                  [_vModelText, firstName.value]
                ]),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((lastName).value = $event)),
                  type: "text",
                  placeholder: "Last name",
                  class: "rounded-xl bg-brand-dark-gray border border-white/10 px-3 py-2.5 text-sm text-white"
                }, null, 512), [
                  [_vModelText, lastName.value]
                ])
              ]),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((discountCode).value = $event)),
                type: "text",
                placeholder: "Discount code (optional)",
                class: "w-full rounded-xl bg-brand-dark-gray border border-white/10 px-3 py-2.5 text-sm text-white"
              }, null, 512), [
                [_vModelText, discountCode.value]
              ]),
              _createElementVNode("label", _hoisted_30, [
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((consentAccepted).value = $event)),
                  type: "checkbox",
                  class: "mt-0.5"
                }, null, 512), [
                  [_vModelCheckbox, consentAccepted.value]
                ]),
                _createElementVNode("span", null, _toDisplayString(consentCopy.value || 'I agree to the free trial and monthly billing terms. My card will be stored for future charges.'), 1)
              ]),
              _createElementVNode("button", {
                type: "button",
                class: "mt-2 w-full flex h-12 items-center justify-center rounded-full bg-brand-lime px-6 text-lg font-medium text-brand-black transition disabled:opacity-50 active:scale-[0.99]",
                disabled: checkingOut.value || polling.value || !consentAccepted.value,
                onClick: checkout
              }, [
                (checkingOut.value || polling.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_32, [
                      _createVNode(_unref(IonSpinner), {
                        name: "crescent",
                        class: "w-5 h-5 text-brand-black"
                      }),
                      _createTextVNode(" " + _toDisplayString(polling.value ? 'Confirming…' : 'Opening checkout…'), 1)
                    ]))
                  : (_openBlock(), _createElementBlock("span", _hoisted_33, "Continue to checkout"))
              ], 8, _hoisted_31),
              _cache[10] || (_cache[10] = _createElementVNode("p", { class: "text-center text-xs text-brand-light-gray/80 leading-relaxed px-2" }, " Secure card checkout opens in this tab. Premium unlocks when your card is stored. ", -1)),
              (error.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_34, _toDisplayString(error.value), 1))
                : _createCommentVNode("", true),
              _createElementVNode("button", {
                type: "button",
                class: "w-full pt-1 text-center text-sm text-brand-light-gray/70 hover:text-brand-light-gray",
                onClick: dismiss
              }, " Not now ")
            ]))
    ])
  ]))
}
}

})